/**
 * Safe score formula evaluator and score computation utilities.
 *
 * Replaces all hardcoded `2 * Math.round((a + b + c + d) / 4)` patterns
 * with a configurable formula stored in service_criteria JSON.
 */

export const DEFAULT_SCORE_FORMULA = "score_multiplier * round(avg(all_percents))";

export interface ScoreFormulaVariables {
  score_multiplier: number;
  metric_percents: number[];
  drink_percent: number;
  all_percents: number[];
  greeting_count: number;
  /**
   * feat-092: number of customer visits (greeting-anchored). This is the new
   * backend denominator (`DEFAULT_SCORE_FORMULA` divides by `visit_count`).
   * Kept alongside `greeting_count` so legacy per-tenant formula overrides that
   * still reference `greeting_count` keep evaluating. The live score is read
   * verbatim from the backend; this var only feeds the client-side preview
   * evaluator so a preview matches the backend formula.
   */
  visit_count: number;
  metric_counts: Record<string, number>;
  drink_count: number;
}

// ============================================================
// Recursive-descent parser for safe expression evaluation
// ============================================================

type TokenType =
  | 'number' | 'ident' | '(' | ')' | '[' | ']' | ','
  | '+' | '-' | '*' | '/' | '//' | '%' | '**'
  | '>' | '>=' | '<' | '<=' | '==' | '!='
  | 'if' | 'else'
  | 'eof';

interface Token {
  type: TokenType;
  value: string | number;
}

const KEYWORDS = new Set(['if', 'else']);

function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < expr.length) {
    const ch = expr[i];
    if (/\s/.test(ch)) { i++; continue; }

    // Numbers
    if (/\d/.test(ch) || (ch === '.' && i + 1 < expr.length && /\d/.test(expr[i + 1]))) {
      let num = '';
      while (i < expr.length && /[\d.]/.test(expr[i])) { num += expr[i++]; }
      tokens.push({ type: 'number', value: parseFloat(num) });
      continue;
    }

    // Identifiers / keywords
    if (/[a-zA-Z_]/.test(ch)) {
      let ident = '';
      while (i < expr.length && /[a-zA-Z0-9_]/.test(expr[i])) { ident += expr[i++]; }
      if (KEYWORDS.has(ident)) {
        tokens.push({ type: ident as TokenType, value: ident });
      } else {
        tokens.push({ type: 'ident', value: ident });
      }
      continue;
    }

    // Two-char operators
    if (i + 1 < expr.length) {
      const two = ch + expr[i + 1];
      if (['>=', '<=', '==', '!=', '//', '**'].includes(two)) {
        tokens.push({ type: two as TokenType, value: two });
        i += 2;
        continue;
      }
    }

    // Single-char operators
    if ('+-*/%()[]>,<'.includes(ch)) {
      tokens.push({ type: ch as TokenType, value: ch });
      i++;
      continue;
    }

    throw new Error(`Unexpected character: ${ch}`);
  }
  tokens.push({ type: 'eof', value: '' });
  return tokens;
}

// Available functions
function safeAvg(values: number[]): number {
  if (!values || values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

const FUNCTIONS: Record<string, (...args: any[]) => any> = {
  round: Math.round,
  avg: safeAvg,
  sum: (arr: number[]) => arr.reduce((a, b) => a + b, 0),
  len: (arr: any[]) => arr.length,
  min: (...args: any[]) => {
    const vals = args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
    return Math.min(...vals);
  },
  max: (...args: any[]) => {
    const vals = args.length === 1 && Array.isArray(args[0]) ? args[0] : args;
    return Math.max(...vals);
  },
  abs: Math.abs,
};

class Parser {
  private tokens: Token[];
  private pos = 0;
  private variables: Record<string, any>;

  constructor(tokens: Token[], variables: Record<string, any>) {
    this.tokens = tokens;
    this.variables = variables;
  }

  private peek(): Token { return this.tokens[this.pos]; }
  private advance(): Token { return this.tokens[this.pos++]; }

  private expect(type: TokenType): Token {
    const tok = this.advance();
    if (tok.type !== type) throw new Error(`Expected ${type}, got ${tok.type}`);
    return tok;
  }

  parse(): number {
    const result = this.parseIfExpr();
    if (this.peek().type !== 'eof') {
      throw new Error(`Unexpected token: ${this.peek().type}`);
    }
    return result;
  }

  // <ifExpr> ::= <comparison> ('if' <comparison> 'else' <ifExpr>)?
  private parseIfExpr(): any {
    const body = this.parseComparison();
    if (this.peek().type === 'if') {
      this.advance(); // consume 'if'
      const test = this.parseComparison();
      this.expect('else');
      const orelse = this.parseIfExpr();
      return test ? body : orelse;
    }
    return body;
  }

  // <comparison> ::= <addSub> (('>' | '>=' | '<' | '<=' | '==' | '!=') <addSub>)*
  private parseComparison(): any {
    let left: any = this.parseAddSub();
    while (['>', '>=', '<', '<=', '==', '!='].includes(this.peek().type)) {
      const op = this.advance().type;
      const right = this.parseAddSub();
      switch (op) {
        case '>': left = left > right; break;
        case '>=': left = left >= right; break;
        case '<': left = left < right; break;
        case '<=': left = left <= right; break;
        case '==': left = left === right; break;
        case '!=': left = left !== right; break;
      }
    }
    return left;
  }

  // <addSub> ::= <mulDiv> (('+' | '-') <mulDiv>)*
  private parseAddSub(): number {
    let left = this.parseMulDiv();
    while (this.peek().type === '+' || this.peek().type === '-') {
      const op = this.advance().type;
      const right = this.parseMulDiv();
      left = op === '+' ? left + right : left - right;
    }
    return left;
  }

  // <mulDiv> ::= <unary> (('*' | '/' | '//' | '%') <unary>)*
  private parseMulDiv(): number {
    let left = this.parseUnary();
    while (['*', '/', '//', '%'].includes(this.peek().type)) {
      const op = this.advance().type;
      const right = this.parseUnary();
      switch (op) {
        case '*': left = left * right; break;
        case '/': left = left / right; break;
        case '//': left = Math.floor(left / right); break;
        case '%': left = left % right; break;
      }
    }
    return left;
  }

  // <unary> ::= ('-' | '+') <unary> | <power>
  private parseUnary(): number {
    if (this.peek().type === '-') {
      this.advance();
      return -this.parseUnary();
    }
    if (this.peek().type === '+') {
      this.advance();
      return this.parseUnary();
    }
    return this.parsePower();
  }

  // <power> ::= <primary> ('**' <unary>)?
  private parsePower(): number {
    let base = this.parsePrimary();
    if (this.peek().type === '**') {
      this.advance();
      const exp = this.parseUnary();
      base = Math.pow(base, exp);
    }
    return base;
  }

  // <primary> ::= number | ident | ident '(' args ')' | '(' expr ')' | '[' elts ']'
  private parsePrimary(): any {
    const tok = this.peek();

    if (tok.type === 'number') {
      this.advance();
      return tok.value as number;
    }

    if (tok.type === 'ident') {
      this.advance();
      const name = tok.value as string;

      // Function call
      if (this.peek().type === '(') {
        this.advance(); // consume '('
        const args: any[] = [];
        if (this.peek().type !== ')') {
          args.push(this.parseIfExpr());
          while (this.peek().type === ',') {
            this.advance();
            args.push(this.parseIfExpr());
          }
        }
        this.expect(')');

        const fn = FUNCTIONS[name];
        if (!fn) throw new Error(`Unknown function: ${name}`);
        return fn(...args);
      }

      // Variable lookup
      if (name in this.variables) return this.variables[name];
      if (name in FUNCTIONS) return FUNCTIONS[name];
      throw new Error(`Unknown variable: ${name}`);
    }

    if (tok.type === '(') {
      this.advance();
      const result = this.parseIfExpr();
      this.expect(')');
      return result;
    }

    if (tok.type === '[') {
      this.advance();
      const elts: any[] = [];
      if (this.peek().type !== ']') {
        elts.push(this.parseIfExpr());
        while (this.peek().type === ',') {
          this.advance();
          elts.push(this.parseIfExpr());
        }
      }
      this.expect(']');
      return elts;
    }

    throw new Error(`Unexpected token: ${tok.type}`);
  }
}

/**
 * Safely evaluate a score formula expression string.
 * On any error, falls back to the default formula. If that also fails, returns 0.
 */
export function evaluateScoreFormula(
  formula: string,
  variables: ScoreFormulaVariables
): number {
  const vars: Record<string, any> = { ...variables };

  function evalExpr(expr: string): number {
    const tokens = tokenize(expr);
    const parser = new Parser(tokens, vars);
    return parser.parse();
  }

  try {
    return evalExpr(formula);
  } catch (e) {
    if (formula !== DEFAULT_SCORE_FORMULA) {
      console.warn(`Score formula "${formula}" failed, using default:`, e);
      try {
        return evalExpr(DEFAULT_SCORE_FORMULA);
      } catch {
        // fall through
      }
    }
    console.error('Default score formula also failed:', e);
    return 0;
  }
}

/**
 * Compute score from metric percentages and optional config.
 *
 * Drop-in replacement for:
 *   2 * Math.round((pm + im + id + dp) / 4)
 */
export function computeScore(
  metricPercents: number[],
  drinkPercent: number,
  scoreMultiplier: number = 2,
  scoreFormula?: string
): number {
  const allPercents = [...metricPercents, drinkPercent];

  if (allPercents.length === 0) return 0;

  const variables: ScoreFormulaVariables = {
    score_multiplier: scoreMultiplier,
    metric_percents: metricPercents,
    drink_percent: drinkPercent,
    all_percents: allPercents,
    greeting_count: 0,
    visit_count: 0,
    metric_counts: {},
    drink_count: 0,
  };

  return evaluateScoreFormula(scoreFormula || DEFAULT_SCORE_FORMULA, variables);
}
